import { useEffect, useRef } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { useStoreContext } from 'features/store';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';

import Currency from 'components/currency';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { primitive } from 'styles/constants';
import { logger } from 'utils/logger';

import { useCheckoutContext } from '../hooks/use-checkout-context';
import { useCheckoutLoadingState } from '../hooks/use-checkout-loading-state';

export const InternalPriceMismatchDiagnostic = () => {
  const enableMinimalCartUi = useMinimalCartUi();
  const enableInternalDiagnosticTools = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
  const hasLoggedRef = useRef(false);
  const { checkoutRequestedSubtotalCents } = useOrderContext();
  const { store } = useStoreContext();
  const { serviceMode: selectedServiceMode } = useServiceModeContext();
  const { serverOrder } = useCheckoutContext();
  const { isOrderPriceLoading } = useCheckoutLoadingState();

  const backendSubtotalCents = serverOrder?.cart?.subTotalCents ?? null;
  const currentStoreNumber = store?.number ?? null;
  const serverOrderStoreNumber =
    serverOrder?.cart?.storeDetails?.storeNumber ??
    serverOrder?.cart?.storeId?.toString?.() ??
    null;
  const serverOrderServiceMode = serverOrder?.cart?.serviceMode ?? null;
  const isServerOrderSettledForCurrentSelection = Boolean(
    currentStoreNumber &&
      serverOrderStoreNumber &&
      selectedServiceMode &&
      serverOrderServiceMode &&
      serverOrderStoreNumber === currentStoreNumber &&
      serverOrderServiceMode === selectedServiceMode
  );
  const hasBothValues = checkoutRequestedSubtotalCents !== null && backendSubtotalCents !== null;
  const isPriceMismatch = hasBothValues && backendSubtotalCents !== checkoutRequestedSubtotalCents;
  const shouldShowDiagnostic =
    !isOrderPriceLoading && isServerOrderSettledForCurrentSelection && isPriceMismatch;

  useEffect(() => {
    if (!shouldShowDiagnostic) {
      hasLoggedRef.current = false;
      return;
    }

    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
      logger.error({
        message: 'Checkout subtotal mismatch detected',
        checkoutRequestedSubtotalCents,
        backendSubtotalCents,
        currentStoreNumber,
        selectedServiceMode,
        serverOrderServiceMode,
        serverOrderStoreNumber,
        serverOrder,
      });
    }
  }, [
    backendSubtotalCents,
    checkoutRequestedSubtotalCents,
    currentStoreNumber,
    selectedServiceMode,
    serverOrder,
    serverOrderServiceMode,
    serverOrderStoreNumber,
    shouldShowDiagnostic,
  ]);

  if (!enableMinimalCartUi || !enableInternalDiagnosticTools || !shouldShowDiagnostic) {
    return null;
  }

  return (
    <View style={styles.container}>
      <Typography style={styles.header}>{t('forInternalUseOnly')}</Typography>
      <View style={styles.lineItem}>
        <Typography style={styles.textBase}>RBI Order ID</Typography>
        <Typography style={styles.textBase}>{serverOrder?.rbiOrderId}</Typography>
      </View>
      <View style={styles.lineItem}>
        <Typography style={styles.textBase}>Status</Typography>
        <Typography style={styles.textBase}>{serverOrder?.status}</Typography>
      </View>
      {serverOrder?.delivery?.status && (
        <View style={styles.lineItem}>
          <Typography style={styles.textBase}>Delivery Status</Typography>
          <Typography style={styles.textBase}>{serverOrder.delivery.status}</Typography>
        </View>
      )}
      <View style={styles.titleContainer}>
        <Icon variant="warning" color={primitive.$white} aria-hidden size="4" />
        <Typography style={styles.title}>{t('priceMismatchDetected')}</Typography>
      </View>
      <View style={styles.lineItem}>
        <Typography style={styles.textBase}>{t('requestedAmount')}</Typography>
        <Currency textProps={{ color: primitive.$white }} amount={checkoutRequestedSubtotalCents} />
      </View>
      <View style={styles.lineItem}>
        <Typography style={styles.textBase}>{t('subtotal')}</Typography>
        <Currency textProps={{ color: primitive.$white }} amount={backendSubtotalCents} />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: primitive.$fieryRed,
    padding: 16,
    marginTop: 16,
  },
  textBase: {
    color: primitive.$white,
  },
  header: {
    color: primitive.$white,
    borderBottomWidth: 1,
    borderBottomColor: primitive.$white,
  },
  title: {
    color: primitive.$white,
    marginLeft: 4,
    marginVertical: 8,
    textTransform: 'uppercase',
  },
  titleContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  lineItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
